import { closestCorners, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ReorderHandle } from "src/base-components/ReorderHandle";

type SortableFieldsProps = {
  items: string[];
  children: React.ReactNode;
  onChangeOrder: (items: { oldIndex: number; newIndex: number }) => void;
  disabled?: boolean;
};

export const SortableFieldsContext: React.FC<SortableFieldsProps> = ({
  items,
  children,
  onChangeOrder,
  disabled,
}) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);

      onChangeOrder({
        oldIndex,
        newIndex,
      });
    }
  };

  return (
    <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <SortableContext
        disabled={disabled}
        items={items}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

export const SortableField: React.FC<{
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ id, children, disabled }) => {
  const { listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} className="flex items-start gap-x-2" style={style}>
      {!disabled && (
        <div className="pt-1">
          <ReorderHandle
            color="text-gray-400"
            listeners={listeners}
            size="xs"
          />
        </div>
      )}
      <div className="min-w-0 max-w-full flex-1">{children}</div>
    </div>
  );
};
